import React, { useMemo } from 'react';
import styles from './M11Hero.module.scss';
import { ModuleContainer } from '../ModuleContainer';
import { M11HeroModule } from '~/lib';
import clsx from 'clsx';
import { Container } from '~/shared/components/Container';
import { WaterMarkLogo, WaterMarkPercent } from './WaterMark/WaterMarks';
import { HeroContent } from '~/shared/components/HeroContent/HeroContent';
import { HeroImage } from '~/shared/components/HeroImage/HeroImage';

export interface CustomCSSProperties extends React.CSSProperties {
    [key: `--${string}`]: string | number;
}

export const M11Hero: React.FC<M11HeroModule> = ({
    trumpet,
    title,
    paragraphText,
    link,
    image,
    imageAltText,
    colorTheme,
    imageOnBackground,
    watermark,
    spacingBottom,
    spacingTop,
    disclaimer,
    titleSize,
    video,
    reverseContent,
    ...rest
}) => {
    const { mainColor, highlightColor, foregroundColor, secondaryColor, secondaryColorOpacity } =
        colorTheme;

    const watermarkElement = useMemo(() => {
        if (!watermark) return null;

        return watermark === 'Logo' ? <WaterMarkLogo /> : <WaterMarkPercent />;
    }, [watermark]);

    const noMedia = !image?.src && !video;

    const memoizedCustomProperties = useMemo<CustomCSSProperties>(
        () => ({
            '--color-main': mainColor,
            '--color-foreground': foregroundColor,
            '--color-watermark-bg': secondaryColor,
            '--color-watermark-opacity': secondaryColorOpacity,
            '--color-image-shadow': highlightColor,
            '--color-bullet-color': mainColor === '#f68a20' ? foregroundColor : '#f68a20',
        }),
        [],
    );

    return (
        <ModuleContainer fullWidth {...rest} style={memoizedCustomProperties}>
            <Container>
                <div
                    className={clsx(styles.wrapperHero, {
                        [styles.noImage]: noMedia,
                    })}
                >
                    <div
                        className={clsx(styles.background, {
                            [styles.noImage]: noMedia,
                        })}
                    >
                        {!imageOnBackground && watermark && (
                            <div
                                className={clsx(styles.watermark, {
                                    [styles.watermarkLogo]: watermark === 'Logo',
                                    [styles.watermarkPercent]: watermark == 'Percentage',
                                    [styles.waterMarkNoImg]: noMedia,
                                })}
                            >
                                {watermarkElement}
                            </div>
                        )}
                    </div>
                    <div
                        className={clsx(styles.heroContainer, {
                            [styles.noImage]: noMedia,
                            [styles.reversed]: reverseContent,
                        })}
                    >
                        <HeroContent
                            customProperties={memoizedCustomProperties}
                            noMedia={noMedia}
                            trumpet={trumpet}
                            title={title}
                            titleSize={titleSize}
                            paragraphText={paragraphText}
                            link={link}
                            foregroundColor={foregroundColor}
                        />

                        {(image?.src || video?.src) && (
                            <HeroImage
                                customProperties={memoizedCustomProperties}
                                image={image}
                                imageAltText={imageAltText}
                                watermark={watermark}
                                imageOnBackground={imageOnBackground}
                                video={video}
                                title={title}
                                watermarkElement={watermarkElement}
                            />
                        )}
                    </div>

                    {disclaimer && (
                        <div
                            className={clsx(styles.disclaimer, {
                                [styles.noImage]: noMedia,
                            })}
                            dangerouslySetInnerHTML={{ __html: disclaimer }}
                        />
                    )}
                </div>
            </Container>
        </ModuleContainer>
    );
};
